import { useGetUserTicketsTemporal } from '@sunrise-ui/api-client';
import { TemporalRelation } from '@sunrise-ui/api/events';
import {
  useCheckoutModalStore,
  useSafeGlobalSearchParams,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import { useEffect, useRef } from 'react';

type CheckoutRedirectParams = {
  checkoutId: string | undefined;
  eventId: string | undefined;
  promoCode: string | undefined;
  ticketTypeId: string | undefined;
};

export const useCheckoutRedirect = () => {
  const { checkoutId, eventId, promoCode, ticketTypeId } =
    useSafeGlobalSearchParams<CheckoutRedirectParams>();
  const { openCheckoutModal } = useCheckoutModalStore();
  const hasProcessed = useRef(false);

  const resetCheckoutParams = () => {
    router.setParams({
      promoCode: undefined,
      ticketTypeId: undefined,
      eventId: undefined,
      checkoutId: undefined,
    } satisfies CheckoutRedirectParams);
  };

  // Check if user already has a ticket for this event
  const { data: userTickets, isLoading: isLoadingTickets } =
    useGetUserTicketsTemporal({
      relation: TemporalRelation.Future,
    });
  const userTicket = userTickets?.find((ticket) => ticket.event_id === eventId);

  useEffect(() => {
    if (hasProcessed.current) return;

    // Payment redirect flow - resume checkout at summary step
    // This handles users returning from Stripe 3DS redirects
    if (checkoutId && eventId) {
      hasProcessed.current = true;
      openCheckoutModal({
        eventId,
        checkoutId,
        currentStep: 'summary',
      });
      resetCheckoutParams();
      return;
    }

    if (isLoadingTickets) return;

    if (!userTicket && (promoCode || ticketTypeId) && eventId) {
      hasProcessed.current = true;
      openCheckoutModal({
        eventId,
        initialPromoCode: promoCode,
        initialTicketTypeId: ticketTypeId,
      });
      resetCheckoutParams();
    }
  }, [checkoutId, eventId, userTicket, isLoadingTickets]);
};
